import { useEffect, useState } from 'react';
import { Loader2, Lightbulb, ThumbsUp, Trash2, Plus, CheckCircle, XCircle } from 'lucide-react';
import {
  featuresApi, FEATURE_STATUS_LABEL, FEATURE_STATUS_COLOR,
  type FeatureRequest, type FeatureStatus,
} from '../api/features';
import { useAuth } from '../lib/auth';
import { fmtTime } from '../lib/format';

export default function FeaturesPage() {
  const { user } = useAuth();
  const isStaff = user?.type === 'staff';
  const [items, setItems] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await featuresApi.list()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onVote = async (id: string) => {
    const updated = await featuresApi.vote(id);
    setItems(prev => prev.map(f => f.id === id ? updated : f));
  };
  const onDelete = async (id: string) => {
    if (!window.confirm('Удалить предложение?')) return;
    await featuresApi.remove(id);
    setItems(prev => prev.filter(f => f.id !== id));
  };
  const onDecide = async (id: string, status: FeatureStatus) => {
    const comment = (status === 'rejected') ? window.prompt('Комментарий (почему отклоняем)?') : null;
    const updated = await featuresApi.decide(id, status, comment || undefined);
    setItems(prev => prev.map(f => f.id === id ? updated : f));
  };

  // Сортировка: по голосам убыв, затем по дате убыв
  const sorted = [...items].sort((a, b) => b.votes - a.votes || (b.created_at > a.created_at ? 1 : -1));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Lightbulb className="text-yellow-400" size={22} /> Предложения и идеи
        </h1>
        <button onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-4 py-2 rounded">
          <Plus size={14} /> Предложить фичу
        </button>
      </div>

      <p className="text-sm text-slate-400 mb-4">
        Предложите доработку SD или соседних сервисов. Голосуйте за чужие — по голосам видно приоритет.
      </p>

      {loading ? (
        <Loader2 className="animate-spin text-blue-400 mx-auto my-10" />
      ) : sorted.length === 0 ? (
        <div className="text-center text-slate-500 py-12">Пока нет предложений.</div>
      ) : (
        <ul className="space-y-3">
          {sorted.map(f => {
            const canDelete = isStaff || (f.requester_email === user?.email && f.status === 'proposed');
            return (
              <li key={f.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                <div className="flex gap-4">
                  <button onClick={() => onVote(f.id)}
                    className={`flex flex-col items-center justify-center w-14 h-16 rounded-lg shrink-0 transition ${
                      f.voted_by_me
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}>
                    <ThumbsUp size={14} />
                    <span className="text-lg font-bold leading-none mt-1">{f.votes}</span>
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-base font-bold text-white">{f.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded border uppercase font-bold ${FEATURE_STATUS_COLOR[f.status]}`}>
                        {FEATURE_STATUS_LABEL[f.status]}
                      </span>
                    </div>
                    {f.description && (
                      <p className="text-sm text-slate-300 whitespace-pre-wrap mb-2">{f.description}</p>
                    )}
                    {f.decision_comment && (
                      <p className="text-xs text-slate-500 italic mb-1">
                        Решение: {f.decision_comment}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-[11px] text-slate-500">
                      <span>{f.requester_name || f.requester_email || 'Гость'} · {fmtTime(f.created_at)}</span>
                      {isStaff && f.status === 'proposed' && (
                        <>
                          <button onClick={() => onDecide(f.id, 'approved')}
                            className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                            <CheckCircle size={11} /> Одобрить
                          </button>
                          <button onClick={() => onDecide(f.id, 'rejected')}
                            className="text-red-400 hover:text-red-300 inline-flex items-center gap-1">
                            <XCircle size={11} /> Отклонить
                          </button>
                        </>
                      )}
                      {isStaff && f.status === 'approved' && (
                        <button onClick={() => onDecide(f.id, 'in_progress')}
                          className="text-blue-400 hover:text-blue-300">→ В разработке</button>
                      )}
                      {isStaff && f.status === 'in_progress' && (
                        <button onClick={() => onDecide(f.id, 'done')}
                          className="text-purple-400 hover:text-purple-300">→ Готово</button>
                      )}
                    </div>
                  </div>

                  {canDelete && (
                    <button onClick={() => onDelete(f.id)}
                      className="text-slate-500 hover:text-red-400 p-1 self-start">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await featuresApi.create({ title, description });
      onCreated();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <form onSubmit={submit} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">Новое предложение</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </header>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Заголовок *</span>
            <input value={title} onChange={e => setTitle(e.target.value)} required minLength={3} maxLength={255}
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Описание</span>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={5} maxLength={10000}
                      className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
        </div>
        <div className="p-4 flex justify-end gap-2 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
          <button type="submit" disabled={busy || title.length < 3}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
            {busy ? 'Отправляю…' : 'Отправить'}
          </button>
        </div>
      </form>
    </div>
  );
}
